/**
 * zod 4 schemas for every trust boundary: client → Firestore, client → Functions,
 * LLM → app. CLAUDE.md #9: validate at the boundary, never trust a payload.
 */
import { z } from 'zod';
export const frameStateSchema = z.enum(['good', 'forwardHead', 'slouch', 'recline', 'invalid']);
export const metricStatsSchema = z.object({
    mean: z.number(),
    p10: z.number(),
    p50: z.number(),
    p90: z.number(),
    n: z.number().int().nonnegative(),
});
export const episodeSchema = z.object({
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    state: z.enum(['forwardHead', 'slouch', 'recline']),
    recovered: z.boolean(),
});
export const sessionSummarySchema = z.object({
    uid: z.string().min(1),
    startedAt: z.number().int(),
    endedAt: z.number().int(),
    durationSec: z.number().nonnegative(),
    validRatio: z.number().min(0).max(1),
    score: z.number().min(0).max(100),
    goodRatio: z.number().min(0).max(1),
    fhpMin: z.number().nonnegative(),
    slouchMin: z.number().nonnegative(),
    reclineMin: z.number().nonnegative(),
    cva: metricStatsSchema.nullable(),
    cha: metricStatsSchema.nullable(),
    trunk: metricStatsSchema.nullable(),
    prot: metricStatsSchema.nullable(),
    hip: metricStatsSchema.nullable(),
    episodes: z.array(episodeSchema).max(50),
    decay: z.array(z.number().min(0).max(1)),
    series: z
        .array(z.object({
        m: z.number().int().min(0),
        cva: z.number().nullable(),
        trunk: z.number().nullable(),
        prot: z.number().nullable(),
        good: z.number().min(0).max(1),
        valid: z.number().min(0).max(1),
    }))
        .max(240)
        .optional(),
    timeToFirst: z.number().nonnegative().nullable(),
    framingScore: z.number().min(0).max(1),
    poseModel: z.enum(['lite', 'full']),
    appVersion: z.string(),
    thresholdsVersion: z.string(),
    classifierVersion: z.string().nullable(),
    storagePath: z.string().nullable(),
    deviceClass: z.enum(['low', 'mid', 'high']),
    sampleFps: z.number().min(1).max(10),
    disagreementCount: z.number().int().nonnegative(),
    phoneRollDeg: z.number().min(-90).max(90).nullable().optional(),
    phonePitchDeg: z.number().min(0).max(90).nullable().optional(),
    actualFps: z.number().min(0).max(60).optional(),
});
export const markedRangeSchema = z.object({
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    reason: z.enum(['eating', 'reaching', 'camera_moved', 'person_passed', 'actually_bad', 'other']),
});
export const sessionFeedbackSchema = z.object({
    uid: z.string().min(1),
    overall: z.enum(['accurate', 'inaccurate']),
    markedRanges: z.array(markedRangeSchema).max(20),
    freeText: z.string().max(1000).optional(),
    createdAt: z.number().int(),
});
export const equipmentSchema = z.object({
    laptop: z.boolean(),
    monitor: z.boolean(),
    chairType: z.enum(['office', 'dining', 'sofa', 'other']),
});
export const userProfileSchema = z.object({
    displayName: z.string().max(80).nullable(),
    birthYear: z.number().int().min(1900).max(new Date().getFullYear()),
    createdAt: z.number().int(),
    tz: z.string(),
    goals: z.array(z.string()).max(5),
    sittingHoursBucket: z.enum(['<4', '4-6', '6-8', '8+']),
    equipment: equipmentSchema,
    heightCm: z.number().min(100).max(250).optional(),
    sex: z.enum(['female', 'male', 'other', 'unspecified']).optional(),
    weightKg: z.number().min(25).max(300).optional(),
});
const dayListSchema = z.array(z.number().int().min(0).max(6)).max(7);
const minuteOfDaySchema = z.number().int().min(0).max(24 * 60);
export const workHoursSchema = z
    .object({ start: minuteOfDaySchema, end: minuteOfDaySchema, days: dayListSchema })
    .refine((w) => w.end > w.start, 'Work hours must end after they start');
export const fixedReminderSchema = z.object({
    id: z.string().min(1).max(40),
    kind: z.enum(['break', 'snack', 'check']),
    minute: minuteOfDaySchema,
    days: dayListSchema.min(1),
});
export const programEnrollmentSchema = z.object({
    programId: z.string().min(1).max(60),
    startedAt: z.number().int(),
    endedAt: z.number().int().nullable(),
});
export const userSettingsSchema = z.object({
    units: z.enum(['auto', 'metric', 'imperial']).optional(),
    reminderEveryDays: z.union([z.literal(3), z.literal(7)]),
    reminderDow: z.number().int().min(0).max(6),
    reminderHour: z.number().int().min(0).max(23),
    alertMode: z.enum(['silent', 'sound', 'vibrate']),
    sampleFps: z.number().min(2).max(5),
    dimByDefault: z.boolean(),
    sessionReminder: z.boolean().optional(),
    reminderDays: z.array(z.number().int().min(0).max(6)).min(1).max(7).optional(),
    exerciseReminder: z.boolean().optional(),
    exerciseReminderHour: z.number().int().min(0).max(23).optional(),
    exerciseReminderDays: z.array(z.number().int().min(0).max(6)).min(1).max(7).optional(),
    // 20 and 60 were in-session choices before 2026-10-16; still valid on old documents.
    breakEveryMin: z.number().int().refine((n) => [0, 20, 30, 40, 45, 50, 60].includes(n)).optional(),
    workHours: workHoursSchema.optional(),
    breakNudges: z.boolean().optional(),
    snackNudges: z.boolean().optional(),
    fixedReminders: z.array(fixedReminderSchema).max(10).optional(),
});
export const userConsentSchema = z.object({
    tosVersion: z.string(),
    privacyVersion: z.string(),
    chdpVersion: z.string(),
    acceptedAt: z.number().int(),
    dataOptIn: z.boolean(),
    dataOptInAt: z.number().int().nullable(),
    dataOptInVersion: z.string().nullable(),
    dataOptInSignature: z.string().max(80).nullable(),
    dataWithdrawnAt: z.number().int().nullable(),
});
export const calibrationSchema = z.object({
    cvaBaseline: z.number().nullable(),
    trunkBaseline: z.number().nullable(),
    protBaseline: z.number().nullable(),
    measuredAt: z.number().int(),
    sampleCount: z.number().int().nonnegative(),
});
export const sessionSummaryDtoSchema = z.object({
    durationMin: z.number().nonnegative(),
    score: z.number().min(0).max(100),
    goodRatio: z.number().min(0).max(1),
    fhpMin: z.number().nonnegative(),
    slouchMin: z.number().nonnegative(),
    reclineMin: z.number().nonnegative(),
    validRatio: z.number().min(0).max(1),
    cvaMean: z.number().nullable(),
    cvaP10: z.number().nullable(),
    chaMean: z.number().nullable(),
    trunkMean: z.number().nullable(),
    protMean: z.number().nullable(),
    hipMean: z.number().nullable(),
    episodeCount: z.number().int().nonnegative(),
    timeToFirstMin: z.number().nonnegative().nullable(),
    decay: z.array(z.number().min(0).max(1)).max(24),
    worstMetric: z.enum(['cva', 'cha', 'trunk', 'prot', 'none']),
});
export const coachingInputSchema = z.object({
    sessionId: z.string().min(1).max(128),
    sessionSummary: sessionSummaryDtoSchema,
    user: z.object({
        goals: z.array(z.string().max(40)).max(5),
        equipment: equipmentSchema,
        sittingHoursBucket: z.enum(['<4', '4-6', '6-8', '8+']),
    }),
    locale: z.literal('en'),
});
/** Shape the LLM must return. Enforced twice: response schema + parse. */
export const coachingOutputSchema = z.object({
    coachingText: z.string().min(1).max(600),
    exerciseIds: z.array(z.string().min(1).max(64)).min(1).max(3),
    rationale: z.string().min(1).max(300),
    safetyFlags: z.array(z.string().max(64)).max(10),
});
export const exerciseSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    tags: z.array(z.enum(['fhp', 'thoracic-ext', 'scap', 'core', 'lbp', 'general', 'asymmetry'])).min(1),
    difficulty: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    durationSec: z.number().int().positive(),
    reps: z.string(),
    cues: z.array(z.string()).length(3),
    contraindicationTags: z.array(z.string()),
    illustration: z.string(),
});
// ── coach (docs/PRODUCT.md §11) ───────────────────────────────────────────────
export const askCoachInputSchema = z.object({
    question: z.string().min(3).max(600),
    topic: z.enum(['weekly-review', 'trend', 'exercises', 'free']),
    tz: z.string().min(1).max(64),
});
export const coachOutputSchema = z.object({
    answer: z.string().min(1).max(1400),
    exerciseIds: z.array(z.string().min(1).max(64)).max(4),
    safetyFlags: z.array(z.string().max(64)).max(10),
});
//# sourceMappingURL=schemas.js.map